import React from 'react';
import { useSelector } from 'react-redux';
import { Avatar, Button, ButtonVariant } from '@brasshq/ui-kit';
import { CommonContext } from 'src/modules/common/context';
import AccountClosurePendingModal from 'src/modules/offboarding/modals/AccountClosurePendingModal';
import ClosureConfirmationModal from 'src/modules/offboarding/modals/ClosureConfirmationModal';
import { Account, ReduxStore } from 'src/types';

import {
  downloadMigrationAccountsSnapshot,
  fetchMigrationAccountSummary,
  sendMigrationWelcome
} from '../../../functions';
import { useMigrationPreview } from '../../../hooks/useMigrationPreview';
import { clearMigrationProvision, getMigrationProvision } from '../../../storage';
import { MigrationAccountSummaryItem } from '../../../types';
import { getMigrationPreviewProvision, MIGRATION_PREVIEW_ACCOUNTS } from '../../../utils';

import styles from './AccountMigration.module.scss';

interface MigrationAccount {
  id: string;
  accountName: string;
  balance: string;
  balanceRaw: number;
  currency: string;
  accountNumber: string;
  bankName: string;
  avatar: {
    firstname: string;
    lastname: string;
  };
}

const getAvatarName = (accountName: string) => {
  const [firstname = 'Business', lastname = 'Account'] = accountName.split(' ');
  return { firstname, lastname };
};

const mapToMigrationAccount = (account: Account): MigrationAccount => ({
  id: account.id,
  accountName: account.name,
  balance: account.available_balance.formatted_display || account.available_balance.formatted,
  balanceRaw: Number(account.available_balance.raw || '0'),
  currency: account.currency,
  accountNumber: account.number,
  bankName: account.bank?.data?.name || '',
  avatar: getAvatarName(account.name)
});

const mapSummaryItemToMigrationAccount = (
  account: MigrationAccountSummaryItem,
  index: number
): MigrationAccount | null => {
  const accountName = account.account_name || account.name;
  const accountNumber = account.account_number || account.number;
  const bankName = account.bank_name || account.bank?.name || '';
  const balanceData = account.available_balance || account.balance;
  const balanceRaw = Number(balanceData?.raw || '0');
  const currency = account.currency || 'NGN';

  if (!accountName || !accountNumber) return null;

  return {
    id: account.id || account.account_id || `${accountName}-${accountNumber}-${index}`,
    accountName,
    accountNumber,
    bankName,
    balance:
      balanceData?.formatted_display ||
      balanceData?.formatted ||
      formatCurrencyAmount(currency, balanceRaw / MINOR_UNIT_SCALE),
    balanceRaw,
    currency,
    avatar: getAvatarName(accountName)
  };
};

const mapPreviewAccounts = (): MigrationAccount[] =>
  MIGRATION_PREVIEW_ACCOUNTS.map((account, index) =>
    mapSummaryItemToMigrationAccount(account, index)
  ).filter((item): item is MigrationAccount => Boolean(item));

const MINOR_UNIT_SCALE = 100;

const formatCurrencyAmount = (currency: string, amount: number) =>
  `${currency} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MIGRATION_CONFIRMATION_MODAL_DATA = {
  title: 'Confirm migration to Paystack MFB',
  description:
    'Enter your one-time passcode to authorize moving your funds to Paystack MFB and closing your Brass account.',
  buttonLabel: 'Confirm and close account',
  buttonVariant: ButtonVariant.Primary,
  successMessage: ''
};

const AccountMigration: React.FC = () => {
  const { profile } = React.useContext(CommonContext);
  const { isPreview } = useMigrationPreview();
  const reduxAccounts = useSelector((state: ReduxStore) => state['module/accounts'].accounts.data);
  const [showClosureModal, setShowClosureModal] = React.useState(false);
  const [showMigrationSuccess, setShowMigrationSuccess] = React.useState(false);
  const [summaryAccounts, setSummaryAccounts] = React.useState<MigrationAccount[] | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = React.useState(false);
  const [isListScrollable, setIsListScrollable] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement>(null);

  const uniqueAccounts = React.useMemo(() => {
    const embeddedAccounts = profile?.customer?.data?.accounts?.data || [];
    const allAccounts = [...embeddedAccounts, ...reduxAccounts];
    return allAccounts.filter(
      (account, index, arr) => arr.findIndex(candidate => candidate.id === account.id) === index
    );
  }, [profile?.customer?.data?.accounts?.data, reduxAccounts]);

  const migrationAccounts = React.useMemo(
    () => uniqueAccounts.map(mapToMigrationAccount),
    [uniqueAccounts]
  );

  const previewAccounts = React.useMemo(() => mapPreviewAccounts(), []);

  const displayedAccounts = React.useMemo(() => {
    if (summaryAccounts && summaryAccounts.length > 0) return summaryAccounts;
    if (migrationAccounts.length > 0) return migrationAccounts;
    if (isPreview) return previewAccounts;
    return [];
  }, [isPreview, migrationAccounts, previewAccounts, summaryAccounts]);

  const totalBalanceDisplay = React.useMemo(() => {
    if (displayedAccounts.length <= 0) return null;
    const totalBalanceInMinorUnits = displayedAccounts.reduce(
      (sum, account) => sum + account.balanceRaw,
      0
    );
    const totalBalance = totalBalanceInMinorUnits / MINOR_UNIT_SCALE;
    return formatCurrencyAmount(displayedAccounts[0].currency, totalBalance);
  }, [displayedAccounts]);

  const migrationIdentifier =
    profile?.customer?.data?.contact_email || profile?.user?.data?.email || '';
  const customerId = profile?.customer?.data?.id || '';
  const provisionedMigration = React.useMemo(() => {
    if (isPreview) return getMigrationPreviewProvision();
    return getMigrationProvision(customerId);
  }, [customerId, isPreview]);

  React.useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    const updateScrollState = () => {
      setIsListScrollable(listElement.scrollHeight > listElement.clientHeight);
    };

    updateScrollState();
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(listElement);

    return () => resizeObserver.disconnect();
  }, [displayedAccounts, isLoadingSummary]);

  React.useEffect(() => {
    if (isPreview || !customerId) return;

    const loadSummary = async () => {
      setIsLoadingSummary(true);
      const response = await fetchMigrationAccountSummary(customerId);
      const rawAccounts = response?.accounts || response?.customer_accounts || [];
      if (rawAccounts.length > 0) {
        const mappedAccounts = rawAccounts
          .map((account, index) => mapSummaryItemToMigrationAccount(account, index))
          .filter((item): item is MigrationAccount => Boolean(item));

        if (mappedAccounts.length > 0) setSummaryAccounts(mappedAccounts);
      }
      setIsLoadingSummary(false);
    };

    loadSummary();
  }, [customerId, isPreview]);

  const closureTransferDetails = React.useMemo(() => {
    const destination = provisionedMigration?.sweep_funds_to;
    if (!destination) return null;
    return {
      bank: destination.bank,
      account_number: destination.account_number,
      name: destination.name
    };
  }, [provisionedMigration?.sweep_funds_to]);

  const handleSubmit = () => {
    if (isPreview || !migrationIdentifier || !closureTransferDetails) return;
    setShowClosureModal(true);
  };

  const handleClosureSuccess = async () => {
    await sendMigrationWelcome(migrationIdentifier, provisionedMigration?.migration_id);
    clearMigrationProvision(customerId);
    setShowClosureModal(false);
    setShowMigrationSuccess(true);
  };

  const handleDownloadSnapshot = () => {
    downloadMigrationAccountsSnapshot(displayedAccounts);
  };

  return (
    <section className={styles.AccountMigration}>
      <div className={styles.AccountMigration_content}>
        <p className={styles.AccountMigration_label}>Step 3</p>
        <h1>Review and confirm migration</h1>
        <p className={styles.AccountMigration_description}>
          Your Brass balances will be merged into a single new Paystack MFB account. Subaccounts and
          recurring mandates do not carry over.
        </p>

        {totalBalanceDisplay && (
          <div className={styles.AccountMigration_total}>
            <span className={styles.AccountMigration_total_label}>
              Total moving to Paystack MFB
            </span>
            <span className={styles.AccountMigration_total_value}>{totalBalanceDisplay}</span>
          </div>
        )}

        <div className={styles.AccountMigration_listHeader}>
          <p className={styles.AccountMigration_listTitle}>Your Brass accounts</p>
          <Button
            type='button'
            variant={ButtonVariant.Secondary}
            rounded
            className={styles.AccountMigration_downloadButton}
            disabled={isLoadingSummary || displayedAccounts.length <= 0}
            onClick={handleDownloadSnapshot}
          >
            Download snapshot
          </Button>
        </div>
        <p className={styles.AccountMigration_listHint}>
          Save a reference of your current setup to recreate subaccounts on Paystack MFB.
        </p>

        <div
          className={`${styles.AccountMigration_listViewport}${
            isListScrollable ? ` ${styles.AccountMigration_listViewport_scrollable}` : ''
          }`}
        >
          <div ref={listRef} className={styles.AccountMigration_list}>
            {displayedAccounts.map(account => (
              <article key={account.id} className={styles.AccountMigration_account}>
                <div className={styles.AccountMigration_account_identity}>
                  <Avatar
                    firstname={account.avatar.firstname}
                    lastname={account.avatar.lastname}
                    className={styles.AccountMigration_account_avatar}
                  />
                  <div className={styles.AccountMigration_account_summary}>
                    <p className={styles.AccountMigration_account_name}>{account.accountName}</p>
                    <p className={styles.AccountMigration_account_number}>
                      {account.bankName ? `${account.bankName} • ` : ''}
                      {account.accountNumber}
                    </p>
                  </div>
                </div>

                <div className={styles.AccountMigration_account_meta}>
                  <p className={styles.AccountMigration_account_balance}>{account.balance}</p>
                </div>
              </article>
            ))}
            {!isLoadingSummary && displayedAccounts.length <= 0 && (
              <p className={styles.AccountMigration_emptyState}>
                No accounts were found for this customer yet.
              </p>
            )}
          </div>
        </div>

        <div className={styles.AccountMigration_actions}>
          <Button
            type='button'
            variant={ButtonVariant.Primary}
            rounded
            className={styles.AccountMigration_submitButton}
            disabled={!isPreview && (!migrationIdentifier || !closureTransferDetails)}
            onClick={handleSubmit}
          >
            Complete migration
          </Button>
          <p className={styles.AccountMigration_footerNote}>
            Funds move to your Paystack MFB account immediately after you confirm.
          </p>
        </div>
      </div>

      <ClosureConfirmationModal
        show={showClosureModal && Boolean(closureTransferDetails)}
        handleClose={() => setShowClosureModal(false)}
        refresh={handleClosureSuccess}
        data={MIGRATION_CONFIRMATION_MODAL_DATA}
        transferDetails={closureTransferDetails || undefined}
        closureType='migration'
      />

      <AccountClosurePendingModal show={showMigrationSuccess} />
    </section>
  );
};

export default AccountMigration;
